import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchCategoryList } from "../../api/category";
import { fetchCheckList } from "../../api/check";
import { fetchPricelistList } from "../../api/pricelist";
import { buyTariff } from "../../api/user";
import { useNotification } from "../Notification/NotificationProvider";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { TariffPlanCard, type BuySelection } from "../TariffPlanCard/TariffPlanCard";
import { useCurrentUserInfo } from "../../hooks/useCurrentUserInfo";
import {
  MSG_FREE_MONTH,
  MSG_FREE_ONCE,
  buildTariffPlanViews,
  usedFreeTariff,
  type TariffPlanView,
} from "../../utils/tariffPlans";
import { getRequestedTariffBadge } from "../../utils/userLimitsFormat";
import "./TariffPlanList.scss";

const MSG_MANUAL_OK = "Запрос отправлен и будет рассмотрен администратором.";
const MSG_AUTO_OK = "Тариф успешно активирован.";

export const TariffPlanList = () => {
  const { showNotification } = useNotification();
  const { userInfo, requestedTariffs } = useCurrentUserInfo();
  const [plans, setPlans] = useState<TariffPlanView[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const freeUsed = usedFreeTariff(userInfo);

  const pendingByPlan = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of requestedTariffs) {
      if (!item.pricelistId) continue;
      map.set(item.pricelistId, getRequestedTariffBadge(item));
    }
    return map;
  }, [requestedTariffs]);

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchPricelistList(), fetchCategoryList(), fetchCheckList()])
      .then(([pricelists, categories, checks]) => {
        if (cancelled) return;
        setPlans(buildTariffPlanViews(pricelists, categories, checks));
        setError(null);
      })
      .catch(() => {
        if (!cancelled) {
          setPlans([]);
          setError("Не удалось загрузить тарифы");
        }
      })
      .finally(() => {
        if (!cancelled) {
          setReady(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleRequest = useCallback(
    (plan: TariffPlanView, selection: BuySelection) => {
      if (pendingByPlan.has(plan.id)) {
        showNotification({
          message: `Этот тариф уже в статусе «${pendingByPlan.get(plan.id)}»`,
          variant: "error",
        });
        return;
      }

      if (plan.isZeroTariff) {
        if (freeUsed) {
          showNotification({ message: MSG_FREE_ONCE, variant: "error" });
          return;
        }
        if (selection.period !== "month" || selection.amount !== 1) {
          showNotification({ message: MSG_FREE_MONTH, variant: "error" });
          return;
        }
      }

      setRequestingId(plan.id);
      buyTariff({
        pricelistId: plan.id,
        period: selection.period,
        amount: selection.amount,
      })
        .then((response) => {
          if (response.paymentType === "bank") {
            if (response.paymentUrl) {
              window.location.assign(response.paymentUrl);
              return;
            }
            showNotification({
              message: "Не удалось получить ссылку на оплату",
              variant: "error",
            });
            setRequestingId(null);
            return;
          }

          if (response.paymentType === "auto_approved") {
            showNotification({ message: MSG_AUTO_OK, variant: "success" });
            setRequestingId(null);
            return;
          }

          showNotification({ message: MSG_MANUAL_OK, variant: "success" });
          setRequestingId(null);
        })
        .catch((err) => {
          setRequestingId(null);
        });
    },
    [freeUsed, pendingByPlan, showNotification]
  );

  if (!ready) {
    return (
      <div className="tariff-plan-list-loading">
        <CircularProgress size="lg" />
      </div>
    );
  }

  if (error) {
    return <p className="tariff-plan-list-empty tariff-plan-list-empty--error">{error}</p>;
  }

  if (plans.length === 0) {
    return <p className="tariff-plan-list-empty">Нет доступных тарифов</p>;
  }

  return (
    <div className="tariff-plan-list">
      {plans.map((plan) => (
        <TariffPlanCard
          key={plan.id}
          plan={plan}
          requesting={requestingId === plan.id}
          freeUsed={freeUsed}
          pendingLabel={pendingByPlan.get(plan.id) ?? null}
          onRequest={(selection) => handleRequest(plan, selection)}
        />
      ))}
    </div>
  );
};
